import databaseService from './database.service';

/**
 * Settings Service for ZyloBill Web App
 * Manages invoice theme and SMTP settings in PouchDB
 */

/**
 * Wait for database to be initialized
 * @param {number} maxWaitMs - Maximum time to wait in milliseconds
 * @returns {Promise<boolean>} True if database is ready, false if timeout
 */
async function waitForDatabase(maxWaitMs = 5000) {
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitMs) {
    if (databaseService.localDB) {
      return true;
    }
    // Wait 100ms before checking again
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  return false;
}

const DEFAULT_THEME = {
  id: null,
  brandColor: "#6366f1",
  fontFamily: "Inter",
  logoUrl: null,
  company: {
    name: "ZyloBill",
    address: "",
    city: "",
    state: "",
    pincode: "",
    gstin: "",
    email: "",
    phone: "",
  },
  showGstBreakdown: true,
  showHsn: true,
  showSignature: false,
  footerNote: "This is a computer-generated invoice and does not require a signature.",
  paymentInstructions: "",
  termsAndConditions: "",
};

const DEFAULT_SMTP = {
  smtp_host: "",
  smtp_port: 587,
  smtp_secure: false,
  smtp_user: "",
  smtp_password: "",
  from_email: "",
  from_name: "",
  reply_to: "",
  enabled: false,
};

export const settingsService = {
  /* ═══════════════════════════════════════════════════════════
     INVOICE THEME SETTINGS
     ═══════════════════════════════════════════════════════════ */

  /**
   * Get active invoice theme
   */
  async getActiveTheme() {
    try {
      // Wait for database to be initialized
      const isReady = await waitForDatabase();
      if (!isReady) {
        console.warn('[Settings Service] Database not ready, returning default theme');
        return DEFAULT_THEME;
      }
      
      // Look for settings document
      const settings = await databaseService.getSettings();
      
      if (settings.length === 0) {
        // No settings found, return default
        return DEFAULT_THEME;
      }

      // Get the first (and should be only) settings document
      const settingsDoc = settings[0];
      
      return {
        id: settingsDoc._id,
        brandColor: settingsDoc.brandColor || DEFAULT_THEME.brandColor,
        fontFamily: settingsDoc.fontFamily || DEFAULT_THEME.fontFamily,
        logoUrl: settingsDoc.logoUrl || null,
        company: {
          name: settingsDoc.company?.name || DEFAULT_THEME.company.name,
          address: settingsDoc.company?.address || "",
          city: settingsDoc.company?.city || "",
          state: settingsDoc.company?.state || "",
          pincode: settingsDoc.company?.pincode || "",
          gstin: settingsDoc.company?.gstin || "",
          email: settingsDoc.company?.email || "",
          phone: settingsDoc.company?.phone || "",
        },
        showGstBreakdown: settingsDoc.showGstBreakdown !== false,
        showHsn: settingsDoc.showHsn !== false,
        showSignature: settingsDoc.showSignature === true,
        footerNote: settingsDoc.footerNote || DEFAULT_THEME.footerNote,
        paymentInstructions: settingsDoc.paymentInstructions || "",
        termsAndConditions: settingsDoc.termsAndConditions || "",
      };
    } catch (error) {
      console.error('[Settings Service] Failed to get active theme:', error);
      return DEFAULT_THEME;
    }
  },

  /**
   * Get default theme
   */
  async getDefaultTheme() {
    return DEFAULT_THEME;
  },

  /**
   * Update both theme and SMTP settings in a single operation
   * This prevents document conflicts when saving both at once
   */
  async updateAllSettings(themeData, smtpData) {
    try {
      // Wait for database to be initialized
      const isReady = await waitForDatabase();
      if (!isReady) {
        throw new Error('Database not initialized. Please wait a moment and try again.');
      }
      
      // Get existing settings document
      const settings = await databaseService.getSettings();
      let settingsDoc = settings.length > 0 ? settings[0] : null;

      // Prepare combined update
      const updatedSettings = {
        // Theme settings
        brandColor: themeData.brandColor,
        fontFamily: themeData.fontFamily,
        logoUrl: themeData.logoUrl,
        company: themeData.company,
        showGstBreakdown: themeData.showGstBreakdown,
        showHsn: themeData.showHsn,
        showSignature: themeData.showSignature,
        footerNote: themeData.footerNote,
        paymentInstructions: themeData.paymentInstructions,
        termsAndConditions: themeData.termsAndConditions,
        // SMTP settings
        smtp_host: smtpData.smtp_host,
        smtp_port: smtpData.smtp_port,
        smtp_secure: smtpData.smtp_secure,
        smtp_user: smtpData.smtp_user,
        smtp_password: smtpData.smtp_password,
        from_email: smtpData.from_email,
        from_name: smtpData.from_name,
        reply_to: smtpData.reply_to,
        smtp_enabled: smtpData.enabled,
      };

      if (settingsDoc) {
        // Update existing document
        const updated = {
          ...settingsDoc,
          ...updatedSettings,
        };
        
        await databaseService.updateDocument(updated);
        
        return { success: true, id: updated._id };
      } else {
        // Create new settings document
        const newSettings = {
          _id: `settings_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'settings',
          ...updatedSettings,
        };
        
        const result = await databaseService.createDocument(newSettings);
        
        return { success: true, id: result.id };
      }
    } catch (error) {
      console.error('[Settings Service] Failed to update all settings:', error);
      throw new Error(`Failed to update settings: ${error.message}`);
    }
  },

  /**
   * Update invoice theme
   */
  async updateTheme(themeId, data) {
    try {
      // Wait for database to be initialized
      const isReady = await waitForDatabase();
      if (!isReady) {
        throw new Error('Database not initialized. Please wait a moment and try again.');
      }
      
      let settingsDoc = null;

      // If themeId provided, try to get existing document
      if (themeId) {
        settingsDoc = await databaseService.readDocument(themeId);
      }

      // If no document found, check if any settings exist
      if (!settingsDoc) {
        const settings = await databaseService.getSettings();
        if (settings.length > 0) {
          settingsDoc = settings[0];
        }
      }

      const updatedSettings = {
        brandColor: data.brandColor,
        fontFamily: data.fontFamily,
        logoUrl: data.logoUrl,
        company: data.company,
        showGstBreakdown: data.showGstBreakdown,
        showHsn: data.showHsn,
        showSignature: data.showSignature,
        footerNote: data.footerNote,
        paymentInstructions: data.paymentInstructions,
        termsAndConditions: data.termsAndConditions,
      };

      if (settingsDoc) {
        // Update existing document
        const updated = {
          ...settingsDoc,
          ...updatedSettings,
        };
        
        await databaseService.updateDocument(updated);
        
        return { success: true, id: updated._id };
      } else {
        // Create new settings document
        const newSettings = {
          _id: `settings_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'settings',
          ...updatedSettings,
        };
        
        const result = await databaseService.createDocument(newSettings);
        
        return { success: true, id: result.id };
      }
    } catch (error) {
      console.error('[Settings Service] Failed to update theme:', error);
      throw new Error(`Failed to update theme: ${error.message}`);
    }
  },

  /* ═══════════════════════════════════════════════════════════
     SMTP SETTINGS
     ═══════════════════════════════════════════════════════════ */

  /**
   * Get SMTP settings
   */
  async getSmtpSettings() {
    try {
      // Wait for database to be initialized
      const isReady = await waitForDatabase();
      if (!isReady) {
        console.warn('[Settings Service] Database not ready, returning default SMTP');
        return DEFAULT_SMTP;
      }
      
      const settings = await databaseService.getSettings();
      
      if (settings.length === 0) {
        return DEFAULT_SMTP;
      }

      const settingsDoc = settings[0];
      
      return {
        smtp_host: settingsDoc.smtp_host || "",
        smtp_port: settingsDoc.smtp_port || 587,
        smtp_secure: settingsDoc.smtp_secure || false,
        smtp_user: settingsDoc.smtp_user || "",
        smtp_password: settingsDoc.smtp_password || "",
        from_email: settingsDoc.from_email || "",
        from_name: settingsDoc.from_name || "",
        reply_to: settingsDoc.reply_to || "",
        enabled: settingsDoc.smtp_enabled || false,
      };
    } catch (error) {
      console.error('[Settings Service] Failed to get SMTP settings:', error);
      return DEFAULT_SMTP;
    }
  },

  /**
   * Save SMTP settings
   */
  async saveSmtpSettings(smtpData) {
    try {
      // Wait for database to be initialized
      const isReady = await waitForDatabase();
      if (!isReady) {
        throw new Error('Database not initialized. Please wait a moment and try again.');
      }
      
      const settings = await databaseService.getSettings();
      let settingsDoc = settings.length > 0 ? settings[0] : null;

      const updatedSmtp = {
        smtp_host: smtpData.smtp_host,
        smtp_port: smtpData.smtp_port,
        smtp_secure: smtpData.smtp_secure,
        smtp_user: smtpData.smtp_user,
        smtp_password: smtpData.smtp_password,
        from_email: smtpData.from_email,
        from_name: smtpData.from_name,
        reply_to: smtpData.reply_to,
        smtp_enabled: smtpData.enabled,
      };

      if (settingsDoc) {
        // Update existing document
        const updated = {
          ...settingsDoc,
          ...updatedSmtp,
        };
        
        await databaseService.updateDocument(updated);
        
        return { success: true, id: updated._id };
      } else {
        // Create new settings document
        const newSettings = {
          _id: `settings_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'settings',
          ...DEFAULT_THEME,
          ...updatedSmtp,
        };
        
        const result = await databaseService.createDocument(newSettings);
        
        return { success: true, id: result.id };
      }
    } catch (error) {
      console.error('[Settings Service] Failed to save SMTP settings:', error);
      throw new Error(`Failed to save SMTP settings: ${error.message}`);
    }
  },

  /**
   * Test SMTP connection
   */
  async testSmtp(smtpData) {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      
      console.log('[Settings Service] Testing SMTP connection to:', API_URL);
      
      // Get auth tokens from sessionStorage (not localStorage!)
      const authToken = sessionStorage.getItem('sessionToken');
      const deviceToken = sessionStorage.getItem('deviceToken');
      
      console.log('[Settings Service] Auth tokens:', {
        hasAuthToken: !!authToken,
        hasDeviceToken: !!deviceToken
      });
      
      if (!authToken || !deviceToken) {
        return {
          success: false,
          message: 'Authentication required. Please log in again.',
        };
      }

      // Validate SMTP data
      if (!smtpData.smtp_host || !smtpData.smtp_user || !smtpData.smtp_password) {
        return {
          success: false,
          message: 'Please fill in all required fields (SMTP Host, Email Address, and App Password).',
        };
      }

      console.log('[Settings Service] Sending test request with SMTP config:', {
        host: smtpData.smtp_host,
        port: smtpData.smtp_port,
        user: smtpData.smtp_user,
        secure: smtpData.smtp_secure
      });

      // Call backend API to test connection
      const response = await fetch(`${API_URL}/api/email/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'X-Device-Token': deviceToken
        },
        body: JSON.stringify({
          smtpConfig: {
            host: smtpData.smtp_host,
            port: smtpData.smtp_port || 587,
            secure: smtpData.smtp_secure || false,
            user: smtpData.smtp_user,
            password: smtpData.smtp_password
          }
        })
      });

      console.log('[Settings Service] Response status:', response.status);

      const data = await response.json();
      
      console.log('[Settings Service] Response data:', data);

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Connection test failed. Please check your settings.',
        };
      }

      return {
        success: true,
        message: data.message || 'Connection successful! Your email settings are working.',
      };
    } catch (error) {
      console.error('[Settings Service] SMTP test failed:', error);
      return {
        success: false,
        message: error.message || 'Failed to test connection. Please check your internet connection.',
      };
    }
  },

  /**
   * Send test email
   * Note: This is a placeholder for web app
   */
  async sendTestEmail(email) {
    console.warn('[Settings Service] Email sending not implemented in web version');
    return {
      success: false,
      message: 'Email sending is not available in web version. Use desktop app for email functionality.',
    };
  },
};

export default settingsService;
